import React from 'react'
import styled, { css } from '@mui/styled-engine-sc'
import { rgba } from 'polished'
import {
  Box,
  Grid,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Chip as MuiChip,
  Menu,
  Stack,
  Typography as MuiTypography,
  IconButton,
  CardProps,
  CardHeader,
  Alert
} from '@mui/material'
import { spacing } from '@mui/system'
import { green, red, blue } from '@mui/material/colors'
import { MoreVertical } from 'react-feather'
import useTheme from '../../../hooks/useTheme'
import { THEMES } from '../../../constants'
import LoadingState from '../../../components/LoadingState'
import { useTranslation } from 'react-i18next'

const Card = styled(MuiCard)<{ illustration?: string }>`
  position: relative;

  ${(props) =>
    props.illustration &&
    props.theme.palette.mode !== 'dark' &&
    css`
      background: ${(props) => rgba(props.theme.palette.primary.main, 0.125)};
      color: ${(props) => props.theme.palette.primary.main};
    `}
`

const Typography = styled(MuiTypography)(spacing)

// const CardHeader = styled(MuiCardHeader)

const CardContent = styled(MuiCardContent)`
  position: relative;

  &:last-child {
    padding-bottom: ${(props) => props.theme.spacing(4)};
  }
`

const Chip = styled(MuiChip)`
  /*
  position: absolute;
  top: 16px;
  right: 16px;
  */
  height: 20px;
  padding: 4px 0;
  font-size: 85%;
  background-color: ${(props) => props.theme.palette.secondary.main};
  color: ${(props) => props.theme.palette.common.white};
  /* margin-bottom: ${(props) => props.theme.spacing(4)}; */

  span {
    padding-left: ${(props) => props.theme.spacing(2)};
    padding-right: ${(props) => props.theme.spacing(2)};
  }
`

const Percentage = styled(MuiTypography)<{
  percentagecolor: string
  illustration?: string
}>`
  span {
    color: ${(props) => props.percentagecolor};
    font-weight: ${(props) => props.theme.typography.fontWeightBold};
    background: ${(props) => rgba(props.percentagecolor, 0.1)};
    padding: 2px;
    border-radius: 3px;
    margin-right: ${(props) => props.theme.spacing(2)};
  }

  ${(props) =>
    props.illustration &&
    props.theme.palette.mode !== 'dark' &&
    css`
      color: ${(props) => rgba(props.theme.palette.primary.main, 0.85)};
    `}
`

const IllustrationImage = styled('img')`
  height: 100px;
  position: absolute;
  right: ${(props) => props.theme.spacing(1)};
  bottom: ${(props) => props.theme.spacing(1)};
  display: none;

  ${(props) => props.theme.breakpoints.between('xs', 'lg')} {
    display: block;
  }

  @media (min-width: 1600px) {
    display: block;
  }
`

interface StatsProps extends Omit<CardProps, 'children'> {
  title: string
  amount: string
  unit?: string
  chip?: string
  percentagetext?: string
  percentageAltText?: string
  percentagecolor?: string
  illustration?: string
  chart?: React.ReactNode
  menu?: React.ReactNode
  loading?: boolean
  error?: unknown
}

const Stats: React.FC<StatsProps> = ({
  title,
  amount,
  unit,
  chip,
  percentagetext,
  percentageAltText,
  percentagecolor,
  illustration,
  chart,
  menu,
  loading = false,
  error,
  ...props
}) => {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  // const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false)
  // const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }
  const { theme } = useTheme()

  const checkAmountType = (amount: string) => {
    if (
      amount === 'ONLINE' ||
      amount === 'Excellent' ||
      amount === 'Very Good' ||
      amount === 'Good' ||
      amount === 'Sub-Device'
    ) {
      return green[500]
    } else if (amount === 'OFFLINE' || amount === 'Low' || amount === 'Very Low') {
      return red[500]
    } else if (amount === 'Bluetooth') {
      return blue[500]
    } else {
      return theme === THEMES.DARK ? '#fffffff2' : '#000000F2'
    }
  }

  // const CardHeaderWithMenu() {}
  // const CardHeaderWithoutMenu() {}

  // /function openMenu(event: React.MouseEvent<HTMLButtonElement>): void {
  //   setAnchorEl(event.currentTarget)
  //   setMenuIsOpen(true)
  // }

  // function closeMenu(event: React.MouseEvent<HTMLButtonElement>): void {
  //   setAnchorEl(event.currentTarget)
  //   setMenuIsOpen(false)
  // }

  return (
    <Card illustration={illustration} {...props}>
      <CardHeader
        title={title}
        action={
          <Stack direction="row" alignItems="center" spacing={{ xs: 2 }}>
            {chip && !illustration && <Chip label={chip} />}

            {menu && (
              <>
                <IconButton onClick={handleClick}>
                  <MoreVertical />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClick={handleClose}
                  onClose={handleClose}
                  elevation={3}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                  }}
                  MenuListProps={{
                    'aria-labelledby': 'basic-button'
                  }}
                >
                  {menu}
                </Menu>
              </>
            )}
          </Stack>
        }
      />
      <CardContent>
        {loading ? (
          <LoadingState />
        ) : error ? (
          <Alert severity="error">{t('somethingWentWrong')}</Alert>
        ) : (
          <>
            <Typography variant="h3" mb={3}>
              <Box fontWeight="fontWeightRegular" style={{ color: checkAmountType(amount) }}>
                {amount}

                {unit && (
                  <Typography
                    fontSize={13}
                    fontWeight="bold"
                    color={'rgba(255, 255, 255, 0.5)'}
                    sx={{ marginTop: '0.125rem' }}
                  >
                    {unit}
                  </Typography>
                )}
              </Box>
            </Typography>

            {percentagetext && percentageAltText && percentagecolor && (
              <Percentage
                variant="subtitle2"
                color="textSecondary"
                percentagecolor={percentagecolor}
                illustration={illustration}
              >
                <span>{percentagetext}</span> {percentageAltText}
              </Percentage>
            )}

            {chart && (
              <Grid container spacing={3} sx={{ marginTop: '1rem' }}>
                <Grid item xs={12}>
                  {chart}
                </Grid>
              </Grid>
            )}
          </>
        )}
      </CardContent>

      {!!illustration && <IllustrationImage src={illustration} alt="Illustration" />}
    </Card>
  )
}

export default Stats
